import { SupportedInputTypeE, SupportedInputTypeEnum } from "@/testmeta.types";
import { lazy } from 'react';
import type { BoxProps, TextInputProps, TextareaProps, MultiSelectProps, RatingProps, ChipProps, FileInputProps, ColorInputProps } from '@mantine/core';;
import type { DateInputProps, TimeGridProps } from '@mantine/dates';

export interface MetaComponent {
    manual?: string;
    hasHelpText?: boolean;
    hasOptions?: boolean;
    validOptions: string[];
    requiredOptions?: string[];
    parseOptions: (options: string[]) => BoxProps | Promise<BoxProps>;
    comp: ReturnType<typeof lazy>;
    defaultProps?: BoxProps;
}

const testMan = `# Valid Options
        - 'placeholder' Sample input to prefill inputs
            - ex) placeholder: "hello world"
        - 'required' Make the input required for submission
        - 'max' Maximum length of the input
            - ex) max: 100
        - 'min' Minimum length of the input
            - ex) min: 10
        - 'left' Left section of the input
            - ex) left: "$"
        - 'right' Right section of the input
            - ex) right: "Months"

    > Note: Any other values entered will be ignored`;
const selectMan = `# Valid Options - Select Type
        - 'from' List of options for the user to choose from
            - ex) from: I Agree, Indifferent, I Disagree
            - *REQUIRED*
        - 'pick' Maximum number of options to select
            - ex) pick: 2
            - [DEFAULT] pick: 1
        - 'min' Minimum number of options to select
            - ex) min: 2
        - 'required' Make the input required for submission
        - 'placeholder' Sample input to prefill inputs
            - ex) placeholder: "hello world"

    > Note: Any value entered will be ignored`;
const fileMan = `# Valid Options - File Input
        - 'required' Make the input required for submission
        - 'accept' File type to accept
            - ex) accept: image/*
            - [DEFAULT] accept: undefined
        - 'ft' File Type list to accept (comma separated)
            - ex) ft: pdf, docx, txt
            - [DEFAULT] ft: pdf
            - Note: 'accept' overrides 'ft'
        
    > Note: Any value entered will be ignored`;
const timeMan = `# Valid Options - Time Input
        - 'required' Make the input required for submission
        - 'ds' List of time options to choose from
            - ex) ds: 12:00, 12:30, 13:00, 13:30, 14:00
            - [DEFAULT] undefined
        - 'start' A 24-hour format start time for the Grid
            - ex) start: 8:00
            - [DEFAULT] start: 9:00
        - 'end' A 24-hour format end time for the Grid
            - You can use start/end or 'ds'
            - ex) end: 14:00
            - [DEFAULT] end: 17:00
        - 'step' The step interval for the Grid
            - ex) step: 1:30 (Each slot is 1 hour and 30 minutes)
            - [DEFAULT] step: 1:00
    > Note: Any value entered will be ignored`;
const generalMan = `# Valid Options - General Input
        - 'required' Make the input required for submission
        - 'placeholder' Sample input to prefill inputs
            - ex) placeholder: "hello world"
    > Note: Any value entered will be ignored`;
const dateMan = `# Valid Options - Date Input
        - 'required' Make the input required for submission
        - 'placeholder' Sample input to prefill inputs
            - ex) placeholder: "hello world"
    > Note: Any value entered will be ignored`;
const ratingMan = `# Valid Options - Rating Input
        - 'required' Make the input required for submission
        - 'fractions' Can be 1 | 2 | 3 | 4 - How many pieces the rating is divided up
            - ex) fractions: 2
            - [DEFAULT] fractions: 1
        - 'count' The number of ratings to display
            - ex) count: 8
            - [DEFAULT] count: 5
    > Note: Any value entered will be ignored`;

export const MetaCompBlks: Record<SupportedInputTypeE, MetaComponent> = {
    'text': {
        hasHelpText: true,
        manual: testMan,
        validOptions: ['placeholder', 'required', 'max', 'min', 'left', 'right'],
        comp: lazy(async () => {
            const { TextInput } = await import('@mantine/core');
            return { default: TextInput };
        }),
        defaultProps: {
            placeholder: 'Enter Text...',
            maxLength: 512,
        } as TextInputProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.text.validOptions;
            const rtn = { ...MetaCompBlks.text.defaultProps } as TextInputProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.text.validOptions[number]) {
                        case 'placeholder':
                            rtn.placeholder = val;
                            break;
                        case 'max':
                            rtn.maxLength = parseInt(val);
                            break;
                        case 'min':
                            rtn.minLength = parseInt(val);
                            break;
                        case 'required':
                            rtn.required = true;
                            break;
                        case 'left':
                            rtn.leftSection = val;
                            break;
                        case 'right':
                            rtn.rightSection = val;
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }

    },
    'long': {
        hasHelpText: true,
        manual: testMan,
        validOptions: ['placeholder', 'required', 'max', 'min'],
        comp: lazy(async () => {
            const { Textarea } = await import('@mantine/core');
            return { default: Textarea };
        }),
        defaultProps: {
            placeholder: 'Enter Text...',
            maxLength: 512,
        } as TextareaProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.long.validOptions;
            const rtn = { ...MetaCompBlks.long.defaultProps } as TextareaProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.long.validOptions[number]) {
                        case 'placeholder':
                            rtn.placeholder = val;
                            break;
                        case 'max':
                            rtn.maxLength = parseInt(val);
                            break;
                        case 'min':
                            rtn.minLength = parseInt(val);
                            break;
                        case 'required':
                            rtn.required = true;
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }
    },
    'select': {
        hasHelpText: true,
        manual: selectMan,
        validOptions: ['from', 'pick', 'required', 'placeholder'],
        requiredOptions: ['from',],
        comp: lazy(async () => {
            const { MultiSelect } = await import('@mantine/core');
            return { default: MultiSelect };
        }),
        defaultProps: {
            data: ['Yes', 'No', 'Ignored'],
            maxValues: 1
        } as MultiSelectProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.select.validOptions;
            const rtn = { ...MetaCompBlks.select.defaultProps } as MultiSelectProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.select.validOptions[number]) {
                        case 'from':
                            rtn.data = val.split(',');
                            break;
                        case 'pick':
                            rtn.maxValues = parseInt(val);
                            break;
                        case 'required':
                            rtn.required = true;
                            break;
                        case 'placeholder':
                            rtn.placeholder = val;
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }
    },
    'rating': {
        hasHelpText: true,
        manual: ratingMan,
        validOptions: ['fractions', 'count', 'required'],
        comp: lazy(async () => {
            const { Rating } = await import('@mantine/core');
            return { default: Rating };
        }),
        defaultProps: {
            fractions: 1,
            count: 5,
        } as RatingProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.rating.validOptions;
            const rtn = { ...MetaCompBlks.rating.defaultProps } as RatingProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.rating.validOptions[number]) {
                        case 'fractions':
                            rtn.fractions = parseInt(val);
                            break;
                        case 'count':
                            rtn.count = parseInt(val);
                            break;

                        case 'required':
                            console.log('ignored here');
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }
    },
    'checkbox': {
        validOptions: [],
        comp: lazy(async () => {
            const { Chip } = await import('@mantine/core');
            return { default: Chip };
        }),
        parseOptions: (_options: string[]) => {
            return {} as BoxProps;
        }
    },
    'date': {
        hasHelpText: true,
        manual: dateMan,
        validOptions: ['placeholder', 'required'],
        comp: lazy(async () => {
            const { DateInput } = await import('@mantine/dates');
            return { default: DateInput };
        }),
        defaultProps: {
            placeholder: 'Enter Date...',
        } as DateInputProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.date.validOptions;
            const rtn = { ...MetaCompBlks.date.defaultProps } as DateInputProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.date.validOptions[number]) {
                        case 'placeholder':
                            rtn.placeholder = val;
                            break;
                        case 'required':
                            rtn.required = true;
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }
    },
    'time': {
        manual: timeMan,
        validOptions: ['ds', 'start', 'end', 'step', 'required'],
        comp: lazy(async () => {
            const { TimeGrid } = await import('@mantine/dates');
            return { default: TimeGrid };
        }),
        defaultProps: {
            data: ['12:00', '12:30', '13:00', '13:30', '14:00',],
        } as TimeGridProps,
        parseOptions: async (options: string[]) => {
            const op = MetaCompBlks.time.validOptions;
            const rtn = { ...MetaCompBlks.time.defaultProps } as TimeGridProps;
            const tro = { startTime: '9:00', endTime: '17:00', interval: '1:00' };
            let flag = false;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.time.validOptions[number]) {
                        case 'ds':
                            rtn.data = val.split(',');
                            break;
                        case 'start':
                            tro.startTime = val;
                            flag = true;
                            break;
                        case 'end':
                            tro.endTime = val;
                            flag = true;
                            break;
                        case 'step':
                            tro.interval = val;
                            flag = true;
                            break;
                        case 'required':
                            console.log('ignored here');
                            break;
                    }
                });
            if (flag) {
                const { getTimeRange } = await import('@mantine/dates');
                rtn.data = getTimeRange({ ...tro });
            }
            return rtn;
        }
    },
    'file': {
        manual: fileMan,
        hasHelpText: true,
        validOptions: ['required', 'accept', 'ft'],
        comp: lazy(async () => {
            const { FileInput } = await import('@mantine/core');
            return { default: FileInput };
        }),
        defaultProps: {
            accept: undefined,
            ft: 'pdf',
        } as FileInputProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.file.validOptions;
            const rtn = { ...MetaCompBlks.file.defaultProps } as FileInputProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.file.validOptions[number]) {
                        case 'accept':
                        case 'ft':
                        default:
                            console.warn(`TODO: Not implemented: ${k}`, val);
                    }
                });
            return rtn;
        }
    },
    'color': {
        hasHelpText: true,
        manual: generalMan,
        validOptions: ['placeholder', 'required'],
        comp: lazy(async () => {
            const { ColorInput } = await import('@mantine/core');
            return { default: ColorInput };
        }),
        defaultProps: {
            placeholder: 'Enter Color...',
        } as ColorInputProps,
        parseOptions: (options: string[]) => {
            const op = MetaCompBlks.color.validOptions;
            const rtn = { ...MetaCompBlks.color.defaultProps } as ColorInputProps;
            options
                .filter(o => op.find(s => o.startsWith(s)))
                .forEach(o => {
                    const [k, ...v] = o.split(':');
                    const val = v.join(':');
                    switch (k as typeof MetaCompBlks.color.validOptions[number]) {
                        case 'placeholder':
                            rtn.placeholder = val;
                            break;
                        case 'required':
                            console.log('ignored here');
                            break;
                        default:
                            console.warn(`Unknown option: ${k}`);
                    }
                });
            return rtn;
        }
    },
}